import { z } from "zod";
import {
    ColumnOptionsSchema,
    ColumnSchema,
    ViewSchema,
    ViewType,
} from "./views";

export { ClientsWorkOverviewSortValue } from "./clientsWorkOverviewSort";

export const TaskProgressCellVariantSchema = z.enum([
    "count",
    "percentage",
    "circle",
    "remaining",
]);
export type TaskProgressCellVariant = z.infer<
    typeof TaskProgressCellVariantSchema
>;

// The deprecated top-level `variant` mirror is dropped instead of rejected when
// it holds an unknown value, so a stale or renamed variant cannot invalidate the
// whole persisted view. Cells resolve the variant from `customVariants` and fall
// back to their default.
export const TaskProgressColumnOptionsSchema = ColumnOptionsSchema.extend({
    variant: TaskProgressCellVariantSchema.optional().catch(undefined),
});

export const EstimatedTimeCellVariantSchema = z.enum([
    "remainingOfTotal",
    "completedOfTotal",
    "remainingOfCompleted",
]);
export type EstimatedTimeCellVariant = z.infer<
    typeof EstimatedTimeCellVariantSchema
>;

export const DEFAULT_ESTIMATED_TIME_CELL_VARIANT: EstimatedTimeCellVariant =
    "remainingOfTotal";

export const EstimatedTimeColumnOptionsSchema = ColumnOptionsSchema.extend({
    variant: EstimatedTimeCellVariantSchema.optional().catch(undefined),
});

export enum ClientsWorkOverviewColumn {
    CLIENT = "client",
    WORKSPACE_NAME = "workspaceName",
    ENTITY = "entity",
    CLOSE_START = "closeStart",
    CLOSE_END = "closeEnd",
    SERVICE_PERIOD = "servicePeriod",
    WORK_START_DATE = "workStartDate",
    WORK_DELIVERY_DATE = "workDeliveryDate",
    CLOSE_STAGE = "closeStage",
    TIME_IN_STAGE = "timeInStage",
    HEALTH = "health",
    PREPARER_REVIEWER = "preparerReviewer",
    MY_ROLE = "myRole",
    TASK_PROGRESS = "taskProgress",
    ESTIMATED_TIME = "estimatedTime",
    APPLICATION_TYPE = "applicationType",
    APPLIED_BY = "appliedBy",
    APPLIED_AT = "appliedAt",
    UNREAD_TASKS = "unreadTasks",
    ACTIONS = "actions",
}

const makeColumnSchema = (id: ClientsWorkOverviewColumn) =>
    ColumnSchema.extend({ id: z.literal(id) });

export const ClientsWorkOverviewViewSchema = ViewSchema.extend({
    type: z.literal(ViewType.CLIENTS_WORK_OVERVIEW),
    columns: z.array(
        z.discriminatedUnion("id", [
            makeColumnSchema(ClientsWorkOverviewColumn.CLIENT),
            makeColumnSchema(ClientsWorkOverviewColumn.WORKSPACE_NAME),
            makeColumnSchema(ClientsWorkOverviewColumn.ENTITY),
            makeColumnSchema(ClientsWorkOverviewColumn.CLOSE_START),
            makeColumnSchema(ClientsWorkOverviewColumn.CLOSE_END),
            makeColumnSchema(ClientsWorkOverviewColumn.SERVICE_PERIOD),
            makeColumnSchema(ClientsWorkOverviewColumn.WORK_START_DATE),
            makeColumnSchema(ClientsWorkOverviewColumn.WORK_DELIVERY_DATE),
            makeColumnSchema(ClientsWorkOverviewColumn.CLOSE_STAGE),
            makeColumnSchema(ClientsWorkOverviewColumn.TIME_IN_STAGE),
            makeColumnSchema(ClientsWorkOverviewColumn.PREPARER_REVIEWER),
            makeColumnSchema(ClientsWorkOverviewColumn.MY_ROLE),
            ColumnSchema.extend({
                id: z.literal(ClientsWorkOverviewColumn.TASK_PROGRESS),
                options: TaskProgressColumnOptionsSchema.nullable().optional(),
            }),
            ColumnSchema.extend({
                id: z.literal(ClientsWorkOverviewColumn.ESTIMATED_TIME),
                options: EstimatedTimeColumnOptionsSchema.nullable().optional(),
            }),
            makeColumnSchema(ClientsWorkOverviewColumn.APPLICATION_TYPE),
            makeColumnSchema(ClientsWorkOverviewColumn.APPLIED_BY),
            makeColumnSchema(ClientsWorkOverviewColumn.APPLIED_AT),
            makeColumnSchema(ClientsWorkOverviewColumn.HEALTH),
            makeColumnSchema(ClientsWorkOverviewColumn.UNREAD_TASKS),
            makeColumnSchema(ClientsWorkOverviewColumn.ACTIONS),
        ]),
    ),
});

export type ClientsWorkOverviewView = z.infer<
    typeof ClientsWorkOverviewViewSchema
>;
